import * as Sentry from "@sentry/react";
import { Navigate, RouterProvider, createBrowserRouter } from "react-router-dom";

import { LoginNavigation } from "@/modules/auth/LoginNavigation";
import InvitationAcceptPage from "@/modules/auth/pages/InvitationAcceptPage";
import VerifyEmailPage from "@/modules/auth/pages/VerifyEmailPage";
import { DashboardNavigation } from "@/modules/dashboard/DashboardNavigation";
import { PublicVerificationNavigation } from "@/modules/verification/PublicVerificationNavigation";
import { VerificationLinkRoute } from "@/modules/verification/VerificationLinkRoute";

import { PrivateRoute } from "./PrivateRoute";
import { PublicRoute } from "./PublicRoute";
import { RouteErrorScreen } from "./RouteErrorScreen";
import { UnverifiedOnlyRoute } from "./UnverifiedOnlyRoute";

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

// The verify route is declared BEFORE `/auth/*` so it wins the match and is
// not gated by PublicRoute (which would redirect an authenticated-but-unverified
// user away to /app).
const router = sentryCreateBrowserRouter([
  {
    // Pathless parent: every route below shares one errorElement, so a render
    // error anywhere shows RouteErrorScreen instead of React Router's raw
    // "Unexpected Application Error!" stack dump.
    errorElement: <RouteErrorScreen />,
    children: [
      {
        path: "/auth/register/verify",
        element: (
          <UnverifiedOnlyRoute>
            <VerifyEmailPage />
          </UnverifiedOnlyRoute>
        ),
      },
      {
        // Invitation magic-link landing. Declared BEFORE `/auth/*` and OUTSIDE PublicRoute (same precedent as
        // register/verify): a user with an active session may be accepting an invite for a different account,
        // and PublicRoute would bounce them to /app before they could.
        path: "/auth/invitations/accept",
        element: <InvitationAcceptPage />,
      },
      {
        path: "/auth/*",
        element: (
          <PublicRoute>
            <LoginNavigation />
          </PublicRoute>
        ),
      },
      {
        path: "/app/*",
        element: (
          <PrivateRoute>
            <DashboardNavigation />
          </PrivateRoute>
        ),
      },
      {
        // Public verification link (marco B). Authorized by the identity token in the
        // URL, NOT a session — declared outside PrivateRoute/PublicRoute by design.
        path: "/verify/:token/*",
        element: (
          <VerificationLinkRoute>
            <PublicVerificationNavigation />
          </VerificationLinkRoute>
        ),
      },
      {
        path: "/*",
        element: <Navigate to="/auth" replace />,
      },
    ],
  },
]);

export const Navigation = () => {
  return <RouterProvider router={router} />;
};
